// @ts-nocheck
import React, { useState, useEffect } from "react";
import {
    Plus,
    CheckCircle,
    AlertCircle,
    Clock,
    Zap,
    Database,
    Users,
    Activity,
    RefreshCw,
} from "lucide-react";
import { integrationRegistry } from "../../services/integrations/registry/IntegrationRegistry";
import { ShopifyIntegrationSetup } from "./ShopifyIntegrationSetup";
import { WooCommerceIntegrationSetup } from "./WooCommerceIntegrationSetup";
import { MagentoIntegrationSetup } from "./MagentoIntegrationSetup";
import { BigCommerceIntegrationSetup } from "./BigCommerceIntegrationSetup";
import { SalesforceIntegrationSetup } from "./SalesforceIntegrationSetup";
import { HubSpotIntegrationSetup } from "./HubSpotIntegrationSetup";
import { PipedriveIntegrationSetup } from "./PipedriveIntegrationSetup";
import { ZohoCRMIntegrationSetup } from "./ZohoCRMIntegrationSetup";
import { FacebookInstagramIntegrationSetup } from "./FacebookInstagramIntegrationSetup";
import { LinkedInIntegrationSetup } from "./LinkedInIntegrationSetup";
import { TwitterIntegrationSetup } from "./TwitterIntegrationSetup";
import CrossChannelCoordinationDashboard from "./CrossChannelCoordinationDashboard";
import MonitoringDashboardDemo from "./MonitoringDashboardDemo";
import WebhookDashboardDemo from "./WebhookDashboardDemo";
// Import integrations to trigger registrations
import "../../services/integrations/crm/index";
import "../../services/integrations/ecommerce/index";
import "../../services/integrations/social/index";
import { useUserInfo, useIntegrations, useConnectApp, EnumIntegrationStatus } from "core";
import AddIntegration from "./add-integration/AddIntegration";
import { SkeletonSwitch, IntegrationAvailableCardSkeleton } from "skeleton";
import { PageContainer } from "../ui/page-container";
import IntegrationsHeader from "./IntegrationsHeader";
import { tone, toneForStatus } from "components/ui/tone";

const IntegrationDashboard = () => {
    const [integrations, setIntegrations] = useState([]);
    const [activeIntegrations, setActiveIntegrations] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedIntegration, setSelectedIntegration] = useState(null);
    const [showShopifySetup, setShowShopifySetup] = useState(false);
    const [showWooCommerceSetup, setShowWooCommerceSetup] = useState(false);
    const [showBigCommerceSetup, setShowBigCommerceSetup] = useState(false);
    const [showSalesforceSetup, setShowSalesforceSetup] = useState(false);
    const [showHubSpotSetup, setShowHubSpotSetup] = useState(false);
    const [showPipedriveSetup, setShowPipedriveSetup] = useState(false);
    const [showZohoCRMSetup, setShowZohoCRMSetup] = useState(false);
    const [showMagentoSetup, setShowMagentoSetup] = useState(false);
    const [showFacebookInstagramSetup, setShowFacebookInstagramSetup] = useState(false);
    const [showLinkedInSetup, setShowLinkedInSetup] = useState(false);
    const [showTwitterSetup, setShowTwitterSetup] = useState(false);
    const [showCrossChannelDashboard, setShowCrossChannelDashboard] = useState(false);
    const [showMonitoringDashboard, setShowMonitoringDashboard] = useState(false);
    const [showWebhookDashboard, setShowWebhookDashboard] = useState(false);
    const [loading, setLoading] = useState(true);


    // ********************* All Queries **********************
    const { data: user, isLoading: userLoading } = useUserInfo();
    const { data: integrationsData, isLoading, error } = useIntegrations();
    const { mutate: connect, isPending } = useConnectApp();

    useEffect(() => {
        if (user) {
            console.log("Amplify current user:", user);
        }
    }, [user, userLoading]);



    useEffect(() => {
        loadIntegrations();
    }, []);

    const loadIntegrations = async () => {
        try {
            setLoading(true);

            // Get available integrations from registry
            const registry = integrationRegistry;
            const registrations = registry.getRegistrations();

            // Mock some active integrations for demo
            const mockActiveIntegrations = [
                {
                    id: "salesforce-demo",
                    name: "Salesforce CRM",
                    type: "crm",
                    platform: "salesforce",
                    status: "active",
                    lastSync: new Date(Date.now() - 1000 * 60 * 15), // 15 minutes ago
                    syncedRecords: 1247,
                    errors: 0,
                    config: {
                        instanceUrl: "https://demo.salesforce.com",
                        sandbox: true,
                    },
                },
                {
                    id: "hubspot-demo",
                    name: "HubSpot CRM",
                    type: "crm",
                    platform: "hubspot",
                    status: "active",
                    lastSync: new Date(Date.now() - 1000 * 60 * 8), // 8 minutes ago
                    syncedRecords: 892,
                    errors: 0,
                    config: {
                        portalId: "12345678",
                        useOAuth: true,
                        scopes: [
                            "contacts",
                            "timeline",
                            "crm.objects.contacts.read",
                            "crm.objects.contacts.write",
                        ],
                    },
                },
                {
                    id: "shopify-demo",
                    name: "Shopify Store",
                    type: "ecommerce",
                    platform: "shopify",
                    status: "active",
                    lastSync: new Date(Date.now() - 1000 * 60 * 3), // 3 minutes ago
                    syncedRecords: 2156,
                    errors: 0,
                    config: {
                        shopDomain: "demo-store",
                        usePrivateApp: true,
                        apiVersion: "2023-10",
                        scopes: [
                            "read_products",
                            "read_customers",
                            "read_orders",
                            "read_inventory",
                        ],
                    },
                },
            ];

            setIntegrations(registrations);
            setActiveIntegrations(mockActiveIntegrations);
        } catch (error) {
            console.error("Failed to load integrations:", error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) =>
  // Delegates to the shared tone system: one status→colour mapping for the
  // whole app, and dark mode comes with it.
  tone(toneForStatus(status)).badge;

    const getStatusIcon = (status) => {
        switch (status) {
            case "active":
            case "connected":
                return <CheckCircle className="w-3.5 h-3.5" />;
            case "error":
                return <AlertCircle className="w-3.5 h-3.5" />;
            case "configuring":
            case "pending":
                return <Clock className="w-3.5 h-3.5" />;
            case "inactive":
                return <Clock className="w-3.5 h-3.5" />;
            case "available":
                return <CheckCircle className="w-3.5 h-3.5" />;
            default:
                return <Clock className="w-3.5 h-3.5" />;
        }
    };

    const getPlatformIcon = (type) => {
        switch (type) {
            case "crm":
                return <Users className="w-6 h-6" />;
            case "ecommerce":
                return <Database className="w-6 h-6" />;
            case "social":
                return <Activity className="w-6 h-6" />;
            default:
                return <Zap className="w-6 h-6" />;
        }
    };

    const handleShopifyIntegrationCreated = (integration) => {
        console.log("Shopify integration created:", integration);
        setShowShopifySetup(false);

        // Add the new integration to active integrations
        const newIntegration = {
            id: `shopify-${Date.now()}`,
            name: `Shopify - ${integration.getConfig().shopDomain}`,
            type: "ecommerce",
            platform: "shopify",
            status: "active",
            lastSync: new Date(),
            syncedRecords: 0,
            errors: 0,
            config: {
                shopDomain: integration.getConfig().shopDomain,
                usePrivateApp: integration.getConfig().usePrivateApp,
                apiVersion: integration.getConfig().apiVersion,
                scopes: integration.getConfig().scopes,
            },
        };

        setActiveIntegrations((prev) => [...prev, newIntegration]);
    };

    const handleShopifySetupCancel = () => {
        setShowShopifySetup(false);
    };

    const handleWooCommerceIntegrationCreated = (integration) => {
        console.log("WooCommerce integration created:", integration);
        setShowWooCommerceSetup(false);

        // Add the new integration to active integrations
        const newIntegration = {
            id: `woocommerce-${Date.now()}`,
            name: `WooCommerce - ${integration.getConfig().storeUrl}`,
            type: "ecommerce",
            platform: "woocommerce",
            status: "active",
            lastSync: new Date(),
            syncedRecords: 0,
            errors: 0,
            config: {
                storeUrl: integration.getConfig().storeUrl,
                consumerKey: integration.getConfig().consumerKey ? "***" : null,
                consumerSecret: integration.getConfig().consumerSecret
                    ? "***"
                    : null,
                apiVersion: integration.getConfig().apiVersion,
                webhooksEnabled: integration.getConfig().webhooksEnabled,
            },
        };

        setActiveIntegrations((prev) => [...prev, newIntegration]);
    };

    const handleWooCommerceSetupCancel = () => {
        setShowWooCommerceSetup(false);
    };

    const handleBigCommerceIntegrationCreated = (integration) => {
        console.log("BigCommerce integration created:", integration);
        setShowBigCommerceSetup(false);

        // Add the new integration to active integrations
        const newIntegration = {
            id: `bigcommerce-${Date.now()}`,
            name: `BigCommerce - ${integration.getConfig().settings.customSettings.storeHash}`,
            type: "ecommerce",
            platform: "bigcommerce",
            status: "active",
            lastSync: new Date(),
            syncedRecords: 0,
            errors: 0,
            config: {
                storeHash:
                    integration.getConfig().settings.customSettings.storeHash,
                apiVersion:
                    integration.getConfig().settings.customSettings.apiVersion,
                webhooksEnabled:
                    integration.getConfig().settings.customSettings
                        .enableWebhooks,
            },
        };

        setActiveIntegrations((prev) => [...prev, newIntegration]);
    };

    const handleBigCommerceSetupCancel = () => {
        setShowBigCommerceSetup(false);
    };

    const handleSalesforceIntegrationCreated = (integration) => {
        console.log("Salesforce integration created:", integration);
        setShowSalesforceSetup(false);

        // Add the new integration to active integrations
        const newIntegration = {
            id: `salesforce-${Date.now()}`,
            name: `Salesforce - ${integration.getConfig().settings.customSettings.instanceUrl}`,
            type: "crm",
            platform: "salesforce",
            status: "active",
            lastSync: new Date(),
            syncedRecords: 0,
            errors: 0,
            config: {
                instanceUrl:
                    integration.getConfig().settings.customSettings.instanceUrl,
                sandbox:
                    integration.getConfig().settings.customSettings.isSandbox,
                apiVersion:
                    integration.getConfig().settings.customSettings.apiVersion,
            },
        };

        setActiveIntegrations((prev) => [...prev, newIntegration]);
    };

    const handleSalesforceSetupCancel = () => {
        setShowSalesforceSetup(false);
    };

    const handleHubSpotIntegrationCreated = (integration) => {
        console.log("HubSpot integration created:", integration);
        setShowHubSpotSetup(false);

        // Add the new integration to active integrations
        const newIntegration = {
            id: `hubspot-${Date.now()}`,
            name: `HubSpot - ${integration.getConfig().settings.customSettings.portalId || "Account"}`,
            type: "crm",
            platform: "hubspot",
            status: "active",
            lastSync: new Date(),
            syncedRecords: 0,
            errors: 0,
            config: {
                portalId:
                    integration.getConfig().settings.customSettings.portalId,
                authMethod:
                    integration.getConfig().settings.customSettings.authMethod,
                useOAuth:
                    integration.getConfig().settings.customSettings
                        .authMethod === "oauth",
            },
        };

        setActiveIntegrations((prev) => [...prev, newIntegration]);
    };

    const handleHubSpotSetupCancel = () => {
        setShowHubSpotSetup(false);
    };

    const handlePipedriveIntegrationCreated = (integration) => {
        console.log("Pipedrive integration created:", integration);
        setShowPipedriveSetup(false);

        // Add the new integration to active integrations
        const newIntegration = {
            id: `pipedrive-${Date.now()}`,
            name: `Pipedrive - ${integration.getConfig().settings.customSettings.companyDomain}`,
            type: "crm",
            platform: "pipedrive",
            status: "active",
            lastSync: new Date(),
            syncedRecords: 0,
            errors: 0,
            config: {
                companyDomain:
                    integration.getConfig().settings.customSettings
                        .companyDomain,
                webhooksEnabled:
                    integration.getConfig().settings.customSettings
                        .enableWebhooks,
            },
        };

        setActiveIntegrations((prev) => [...prev, newIntegration]);
    };

    const handlePipedriveSetupCancel = () => {
        setShowPipedriveSetup(false);
    };

    const handleZohoCRMIntegrationCreated = (integration) => {
        console.log("Zoho CRM integration created:", integration);
        setShowZohoCRMSetup(false);

        // Add the new integration to active integrations
        const newIntegration = {
            id: `zohocrm-${Date.now()}`,
            name: `Zoho CRM - ${integration.getConfig().settings.customSettings.dataCenter.toUpperCase()}`,
            type: "crm",
            platform: "zoho",
            status: "active",
            lastSync: new Date(),
            syncedRecords: 0,
            errors: 0,
            config: {
                dataCenter:
                    integration.getConfig().settings.customSettings.dataCenter,
                clientId:
                    integration
                        .getConfig()
                        .settings.customSettings.clientId.substring(0, 8) +
                    "...",
            },
        };

        setActiveIntegrations((prev) => [...prev, newIntegration]);
    };

    const handleZohoCRMSetupCancel = () => {
        setShowZohoCRMSetup(false);
    };

    const handleMagentoIntegrationCreated = (integration) => {
        console.log("Magento integration created:", integration);
        setShowMagentoSetup(false);

        // Add the new integration to active integrations
        const newIntegration = {
            id: `magento-${Date.now()}`,
            name: `Magento 2 - ${integration.getConfig().settings.customSettings.baseUrl}`,
            type: "ecommerce",
            platform: "magento",
            status: "active",
            lastSync: new Date(),
            syncedRecords: 0,
            errors: 0,
            config: {
                baseUrl:
                    integration.getConfig().settings.customSettings.baseUrl,
                storeCode:
                    integration.getConfig().settings.customSettings.storeCode,
                version:
                    integration.getConfig().settings.customSettings.version,
                enableGraphQL:
                    integration.getConfig().settings.customSettings
                        .enableGraphQL,
                webhooksEnabled:
                    !!integration.getConfig().settings.customSettings
                        .webhookSecret,
            },
        };

        setActiveIntegrations((prev) => [...prev, newIntegration]);
    };

    const handleMagentoSetupCancel = () => {
        setShowMagentoSetup(false);
    };

    const handleFacebookInstagramIntegrationCreated = (integrationId) => {
        console.log("Facebook/Instagram integration created:", integrationId);
        setShowFacebookInstagramSetup(false);

        // Add the new integration to active integrations
        const newIntegration = {
            id: integrationId,
            name: "Facebook & Instagram",
            type: "social_media",
            platform: "facebook_instagram",
            status: "active",
            lastSync: new Date(),
            syncedRecords: 0,
            errors: 0,
            config: {
                enableFacebook: true,
                enableInstagram: true,
                audienceSyncEnabled: true,
                engagementTrackingEnabled: true,
            },
        };

        setActiveIntegrations((prev) => [...prev, newIntegration]);
    };

    const handleFacebookInstagramSetupCancel = () => {
        setShowFacebookInstagramSetup(false);
    };

    const handleLinkedInIntegrationCreated = (integration) => {
        console.log("LinkedIn integration created:", integration);
        setShowLinkedInSetup(false);

        // Add the new integration to active integrations
        const newIntegration = {
            id: `linkedin-${Date.now()}`,
            name: `LinkedIn Marketing - ${integration.getConfig().name}`,
            type: "social",
            platform: "linkedin",
            status: "active",
            lastSync: new Date(),
            syncedRecords: 0,
            errors: 0,
            config: {
                enableOrganicPosting:
                    integration.getConfig().settings.enableOrganicPosting,
                enableAnalytics:
                    integration.getConfig().settings.enableAnalytics,
                timezone: integration.getConfig().syncSchedule.timezone,
                businessHours:
                    integration.getConfig().settings.postingSchedule
                        ?.businessHours,
            },
        };

        setActiveIntegrations((prev) => [...prev, newIntegration]);
    };

    const handleLinkedInSetupCancel = () => {
        setShowLinkedInSetup(false);
    };

    const handleTwitterIntegrationCreated = (integration) => {
        console.log("Twitter integration created:", integration);
        setShowTwitterSetup(false);

        // Add the new integration to active integrations
        const newIntegration = {
            id: `twitter-${Date.now()}`,
            name: `Twitter (X) - ${integration.getConfig().name}`,
            type: "social",
            platform: "twitter",
            status: "active",
            lastSync: new Date(),
            syncedRecords: 0,
            errors: 0,
            config: {
                enableTweeting: integration.getConfig().settings.enableTweeting,
                enableAnalytics:
                    integration.getConfig().settings.enableAnalytics,
                enableThreads: integration.getConfig().settings.enableThreads,
                maxTweetsPerHour:
                    integration.getConfig().settings.rateLimitSettings
                        .tweetsPerHour,
            },
        };

        setActiveIntegrations((prev) => [...prev, newIntegration]);
    };

    const handleTwitterSetupCancel = () => {
        setShowTwitterSetup(false);
    };

    const handleConfigureIntegration = (integration) => {
        setSelectedIntegration(integration);
        // In a real app, this would open a configuration modal
        console.log("Configuring integration:", integration);
    };

    const handleSyncIntegration = async (integrationId) => {
        // In a real app, this would trigger a sync
        console.log("Syncing integration:", integrationId);

        // Update last sync time for demo
        setActiveIntegrations((prev) =>
            prev.map((integration) =>
                integration.id === integrationId
                    ? { ...integration, lastSync: new Date() }
                    : integration,
            ),
        );
    };

    const formatLastSync = (date) => {
        const now = new Date();
        const diff = now - date;
        const minutes = Math.floor(diff / (1000 * 60));
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));

        if (minutes < 1) return "Just now";
        if (minutes < 60) return `${minutes}m ago`;
        if (hours < 24) return `${hours}h ago`;
        return `${days}d ago`;
    };

    const connectedList = integrationsData?.filter(
        (i) => i.connectionStatus === EnumIntegrationStatus.Connected,
    ) ?? [];
    const activeCount = connectedList.length;

    /* `availableCount` counted EVERY integration while the grid below renders
       only the not-yet-connected ones, so the tile read "Available 7" above
       five cards. It now counts the same rows the grid does. */
    const availableCount = (integrationsData?.length ?? 0) - activeCount;

    /* `totalSynced` and `errorCount` were literal `null`, so both tiles could
       only ever show an em-dash. The integrations payload carries no synced
       count and no error count, so there is nothing to read — passing null
       keeps the tiles honest, and IntegrationsHeader now omits a tile it has
       no number for rather than rendering a permanent placeholder. */
    const totalSynced = null;
    const errorCount = null;

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-400" />
                <span className="ml-2 text-[13px] font-medium text-content-secondary dark:text-gray-300">
                    Loading integrations...
                </span>
            </div>
        );
    }

    return (
        <PageContainer>
            <IntegrationsHeader
                activeIntegrations={activeCount}
                totalSynced={totalSynced}
                availableIntegrations={availableCount}
                syncErrors={errorCount}
                onAddIntegration={() => setShowAddModal(true)}
                isLoading={isLoading}
            />

            <AddIntegration
                open={showAddModal}
                setOpen={setShowAddModal}
            />

            {/* Available Integrations */}
            <div>
                <div className="flex items-center gap-2.5 mb-4">
                    <h2 className="text-[15px] font-semibold text-gray-800 dark:text-gray-100">
                        Available Integrations
                    </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">

                    <SkeletonSwitch isLoading={isLoading}
                        skeleton={<>
                            {Array.from({ length: 6 }).map((_, i) => (
                                <IntegrationAvailableCardSkeleton key={i} />
                            ))}
                        </>}
                    >
                        {integrationsData?.filter((i) => i.connectionStatus !== EnumIntegrationStatus.Connected).map((integration) => {
                            const connected = integration.connectionStatus === "connected";
                            const pending = integration.connectionStatus === "pending";
                            const statusText = connected ? "connected" : pending ? "pending" : "available";

                            const typeLabel = integration.type ?? integration.name; // new data may not have `type`
                            const caps: string[] = integration.capabilities ?? [];

                            return (
                                <div
                                    key={integration.id}
                                    className="relative overflow-hidden bg-surface dark:bg-dark-800 p-5 rounded-xl shadow-sm border border-line dark:border-dark-700 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex flex-col"
                                >
                                    {/* Accent top bar */}
                                    <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-blue-500 to-indigo-500" />
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            {/* prefer logoUrl from new data; fallback to your icon helper */}
                                            {integration.logoUrl ? (
                                                <div className="w-9 h-9 rounded-lg bg-gray-50 dark:bg-dark-700 border border-line-subtle dark:border-dark-600 flex items-center justify-center overflow-hidden">
                                                    <img
                                                        src={integration.logoUrl}
                                                        alt={integration.name}
                                                        className="w-6 h-6 object-contain"
                                                    />
                                                </div>
                                            ) : (
                                                <div className="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600 dark:text-blue-400">
                                                    {getPlatformIcon?.(integration.type || integration.name)}
                                                </div>
                                            )}
                                            <div>
                                                <h3 className="text-[13px] font-semibold text-content dark:text-gray-100">{integration.name}</h3>
                                                <p className="text-[12px] font-medium text-content-muted dark:text-gray-400 capitalize">{typeLabel}</p>
                                            </div>
                                        </div>
                                        <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold flex items-center gap-1 ${getStatusColor(statusText)}`}>
                                            {getStatusIcon(statusText)}
                                            <span className="capitalize">{statusText}</span>
                                        </span>
                                    </div>

                                    <div className="mb-4 flex-1">
                                        <p className="text-[11px] font-semibold uppercase tracking-wide text-content-subtle dark:text-gray-500 mb-2">Capabilities</p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {caps.slice(0, 3).map((cap) => (
                                                <span
                                                    key={cap}
                                                    className="px-2 py-0.5 bg-gray-100 dark:bg-dark-700 text-content-secondary dark:text-gray-400 text-[11px] font-medium rounded-md border border-line dark:border-dark-600"
                                                >
                                                    {cap.replaceAll("_", " ")}
                                                </span>
                                            ))}
                                            {caps.length > 3 && (
                                                <span className="px-2 py-0.5 bg-gray-100 dark:bg-dark-700 text-content-muted dark:text-gray-400 text-[11px] font-medium rounded-md border border-line dark:border-dark-600">
                                                    +{caps.length - 3} more
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => connect(integration.name)}
                                        disabled={connected || pending}
                                        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-[13px] font-semibold py-2.5 px-4 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        <Plus className="w-4 h-4" />
                                        {pending ? "Connecting…" : connected ? "Connected" : "Connect"}
                                    </button>
                                </div>
                            );
                        })}
                    </SkeletonSwitch>
                </div>
            </div>





            {/* Shopify Integration Setup Modal */}
            {showShopifySetup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-surface rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <ShopifyIntegrationSetup
                            onIntegrationCreated={
                                handleShopifyIntegrationCreated
                            }
                            onCancel={handleShopifySetupCancel}
                        />
                    </div>
                </div>
            )}

            {/* WooCommerce Integration Setup Modal */}
            {showWooCommerceSetup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-surface rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <WooCommerceIntegrationSetup
                            onIntegrationCreated={
                                handleWooCommerceIntegrationCreated
                            }
                            onCancel={handleWooCommerceSetupCancel}
                        />
                    </div>
                </div>
            )}

            {/* BigCommerce Integration Setup Modal */}
            {showBigCommerceSetup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-surface rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <BigCommerceIntegrationSetup
                            onIntegrationCreated={
                                handleBigCommerceIntegrationCreated
                            }
                            onCancel={handleBigCommerceSetupCancel}
                        />
                    </div>
                </div>
            )}

            {/* Salesforce Integration Setup Modal */}
            {showSalesforceSetup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-surface rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <SalesforceIntegrationSetup
                            onIntegrationCreated={
                                handleSalesforceIntegrationCreated
                            }
                            onCancel={handleSalesforceSetupCancel}
                        />
                    </div>
                </div>
            )}

            {/* HubSpot Integration Setup Modal */}
            {showHubSpotSetup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-surface rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <HubSpotIntegrationSetup
                            onIntegrationCreated={
                                handleHubSpotIntegrationCreated
                            }
                            onCancel={handleHubSpotSetupCancel}
                        />
                    </div>
                </div>
            )}

            {/* Pipedrive Integration Setup Modal */}
            {showPipedriveSetup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-surface rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <PipedriveIntegrationSetup
                            onIntegrationCreated={
                                handlePipedriveIntegrationCreated
                            }
                            onCancel={handlePipedriveSetupCancel}
                        />
                    </div>
                </div>
            )}

            {/* Zoho CRM Integration Setup Modal */}
            {showZohoCRMSetup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-surface rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <ZohoCRMIntegrationSetup
                            onIntegrationCreated={
                                handleZohoCRMIntegrationCreated
                            }
                            onCancel={handleZohoCRMSetupCancel}
                        />
                    </div>
                </div>
            )}

            {/* Magento Integration Setup Modal */}
            {showMagentoSetup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-surface rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
                        <div className="p-6">
                            <MagentoIntegrationSetup
                                onComplete={handleMagentoIntegrationCreated}
                                onCancel={handleMagentoSetupCancel}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Facebook Instagram Integration Setup Modal */}
            {showFacebookInstagramSetup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-surface rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <FacebookInstagramIntegrationSetup
                            onComplete={
                                handleFacebookInstagramIntegrationCreated
                            }
                            onCancel={handleFacebookInstagramSetupCancel}
                        />
                    </div>
                </div>
            )}

            {/* LinkedIn Integration Setup Modal */}
            {showLinkedInSetup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-surface rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <LinkedInIntegrationSetup
                            onIntegrationCreated={
                                handleLinkedInIntegrationCreated
                            }
                            onCancel={handleLinkedInSetupCancel}
                        />
                    </div>
                </div>
            )}

            {/* Twitter Integration Setup Modal */}
            {showTwitterSetup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-surface rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <TwitterIntegrationSetup
                            onIntegrationCreated={
                                handleTwitterIntegrationCreated
                            }
                            onCancel={handleTwitterSetupCancel}
                        />
                    </div>
                </div>
            )}

            {/* Cross-Channel Coordination Dashboard Modal */}
            {showCrossChannelDashboard && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-surface rounded-lg shadow-xl max-w-7xl w-full max-h-[95vh] overflow-y-auto">
                        <div className="p-4 border-b flex items-center justify-between">
                            <h2 className="text-xl font-semibold text-content">
                                Cross-Channel Coordination Dashboard
                            </h2>
                            <button
                                onClick={() =>
                                    setShowCrossChannelDashboard(false)
                                }
                                className="text-content-muted hover:text-gray-700 dark:hover:text-gray-300"
                            >
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>
                        <CrossChannelCoordinationDashboard />
                    </div>
                </div>
            )}

            {/* Monitoring Dashboard Modal */}
            {showMonitoringDashboard && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-surface rounded-lg shadow-xl max-w-7xl w-full max-h-[95vh] overflow-y-auto">
                        <div className="p-4 border-b flex items-center justify-between">
                            <h2 className="text-xl font-semibold text-content">
                                Integration Monitoring Dashboard
                            </h2>
                            <button
                                onClick={() =>
                                    setShowMonitoringDashboard(false)
                                }
                                className="text-content-muted hover:text-gray-700 dark:hover:text-gray-300"
                            >
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>
                        <div className="bg-gray-50 dark:bg-dark-700">
                            <MonitoringDashboardDemo />
                        </div>
                    </div>
                </div>
            )}

            {/* Webhook Management Dashboard Modal */}
            {showWebhookDashboard && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-surface rounded-lg shadow-xl max-w-7xl w-full max-h-[95vh] overflow-y-auto">
                        <div className="p-4 border-b flex items-center justify-between">
                            <h2 className="text-xl font-semibold text-content">
                                Webhook Management Dashboard
                            </h2>
                            <button
                                onClick={() => setShowWebhookDashboard(false)}
                                className="text-content-muted hover:text-gray-700 dark:hover:text-gray-300"
                            >
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>
                        <div className="bg-gray-50 dark:bg-dark-700">
                            <WebhookDashboardDemo />
                        </div>
                    </div>
                </div>
            )}
        </PageContainer>
    );
};

export default IntegrationDashboard;
